import { jwtDecode } from 'jwt-decode';
import Cache from './Cache';

interface JwtClaims {
    [key: string]: any;
    UserId?: string;
    StaffId?: string;
    AdminId?: string;
    Email?: string;
    TenantId?: string;
    TenantKey?: string;
    SchemaName?: string;
    HasSchema?: string;
    admin?: string;
    IsImpersonating?: string;
    ImpersonatedBy?: string;
    ImpersonationLogId?: string;
    exp?: number;
}

class TokenHelper {
    /**
     * Decode JWT token and extract claims
     */
    private decodeToken(token: string): JwtClaims | null {
        try {
            return jwtDecode<JwtClaims>(token);
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }

    /**
     * Get current bearer token from cache
     */
    async getToken(): Promise<string | null> {
        return await Cache.get('bearer');
    }

    /**
     * Get decoded claims from cached token
     */
    async getClaims(): Promise<JwtClaims | null> {
        const token = await this.getToken();
        if (!token) return null;
        return this.decodeToken(token);
    }

    /**
     * Check if current user is a system admin
     */
    async isSystemAdmin(): Promise<boolean> {
        const claims = await this.getClaims();
        return claims?.admin === 'sysadmin';
    }

    /**
     * Check if currently impersonating
     */
    async isImpersonating(): Promise<boolean> {
        const claims = await this.getClaims();
        return claims?.IsImpersonating === 'true';
    }

    /**
     * Get admin ID from token (for impersonation)
     */
    async getAdminId(): Promise<string | null> {
        const claims = await this.getClaims();
        return claims?.AdminId || claims?.StaffId || null;
    }

    /**
     * Get impersonation log ID (for ending impersonation)
     */
    async getImpersonationLogId(): Promise<string | null> {
        const claims = await this.getClaims();
        return claims?.ImpersonationLogId || null;
    }

    /**
     * Get tenant context from token
     */
    async getTenantContext(): Promise<{
        tenantId: number;
        tenantKey: string;
        schemaName: string;
        hasSchema: boolean;
    } | null> {
        const claims = await this.getClaims();
        if (!claims) return null;

        return {
            tenantId: parseInt(claims.TenantId || '1'),
            tenantKey: claims.TenantKey || 'main',
            schemaName: claims.SchemaName || 'dbo',
            hasSchema: claims.HasSchema === 'true'
        };
    }

    /**
     * Get user ID from token
     */
    async getUserId(): Promise<string | null> {
        const claims = await this.getClaims();
        return claims?.UserId || claims?.StaffId || null;
    }

    /**
     * Get user email from token
     */
    async getUserEmail(): Promise<string | null> {
        const claims = await this.getClaims();
        return claims?.Email || null;
    }

    /**
     * Decode a JWT token string into a SessionModel-compatible object.
     * Used to populate radar_user from an impersonation token without a server round-trip.
     */
    decodeSessionFromToken(token: string): { UserId?: number | null; StaffId?: string | null; Email?: string | null; Name?: string | null; Roles?: any; Claims?: any } | null {
        try {
            const claims = this.decodeToken(token);
            if (!claims) return null;

            const isStaff = !!claims['_staff'];
            const rawId = claims['_id'];
            const email = claims['_email'] || null;

            let roles: Record<string, any> | null = null;
            let userClaims: Record<string, any> | null = null;

            try { roles = claims['_roles'] ? JSON.parse(claims['_roles']) : null; } catch { roles = null; }
            try { userClaims = claims['_claims'] ? JSON.parse(claims['_claims']) : null; } catch { userClaims = null; }

            if (isStaff) {
                return { StaffId: rawId || null, Email: email, Roles: roles, Claims: userClaims };
            } else {
                return { UserId: rawId ? parseInt(rawId, 10) : null, Email: email, Roles: roles, Claims: userClaims };
            }
        } catch (error) {
            console.error('Error decoding session from token:', error);
            return null;
        }
    }

    /**
     * Check if token is expired
     */
    async isTokenExpired(): Promise<boolean> {
        const claims = await this.getClaims();
        if (!claims || !claims.exp) return true;
        
        const currentTime = Math.floor(Date.now() / 1000);
        return claims.exp < currentTime;
    }

    /**
     * Get token expiration date
     */
    async getTokenExpiration(): Promise<Date | null> {
        const claims = await this.getClaims();
        if (!claims || !claims.exp) return null;
        
        return new Date(claims.exp * 1000);
    }
}

export default new TokenHelper();
